/* ============================================================
   PureTurf product-page coverage calculator.

   WHAT IT DOES
   Given a lawn or field size, finds the CHEAPEST combination of the
   product's real bag sizes that covers it, allowing mixed sizes.

   14 SEP 2026: SEPARATE RATES, AND ACRES FOR FIELDS
   The markup now passes data-rate-new and data-rate-over (sq ft per
   lb) from snippets/pt-seed-rates.liquid. This used to take one rate
   and DOUBLE it for overseeding, which is wrong for TWCA Sun & Shade:
   the rates sheet has it overseeding at HALF its new rate. Pass both.

   data-unit="acres" (Farm & Pasture products) makes the input and every
   displayed area use acres instead of sq ft. Internally everything is
   still sq ft; the unit only converts at the edges (43,560 sq ft/acre).
   data-noun ("lawn" or "field") drives the wording.

   WHY IT WAS REWRITTEN (Aug 12 2026)
   The original picked a size by LEAST LEFTOVER COVERAGE using one
   size at a time. It never mixed sizes and never looked at price.
   On TWCA Sun & Shade that produced:
     17,000 sq ft -> "9 x 10 lb bags, $404.91"
   when 1 x 50 + 1 x 25 + 1 x 10 covers it EXACTLY for $314.96.
   Nine small bags won because they wasted less coverage, while
   costing $90 more. It optimised the wrong thing.

   RANKING (in order)
     1. lowest total cost
     2. then least leftover coverage
     3. then fewest bags

   SIMPLICITY NUDGE
   A single-size answer is easier to buy, so it is preferred when
   it costs no more than SIMPLICITY_TOLERANCE above the cheapest
   mix - but ONLY if it does not need MORE bags. Without that
   guard the nudge fired at 15,000 sq ft and returned 3 x 25 lb
   (3 bags) over 1 x 50 + 1 x 25 (2 bags) at identical cost.
   "One size" is not the same thing as "simpler".

   SEARCH COMPLETENESS
   solve() enumerates counts for every size except the smallest,
   then tops up with the smallest. Buying MORE of the smallest
   than needed can only add cost and waste, so the top-up is
   always exact and nothing optimal is skipped. Verified against
   an exhaustive brute force across the full input range.

   Sizes and prices come from the product's real variants via
   data-sizes, so this improves automatically as the catalogue
   changes. Unpriced products fall back to least-waste ranking.
   ============================================================ */
(function () {
  if (window.__ptProductCalc) return;
  window.__ptProductCalc = true;

  var SIMPLICITY_TOLERANCE = 5.00; // dollars
  var MAX_COMBOS = 200000;
  var SQFT_PER_ACRE = 43560;

  var fmtInt = function (n) { return Math.round(n).toLocaleString(); };
  var fmtUSD = function (n) { return '$' + n.toFixed(2); };
  var round2 = function (n) { return Math.round(n * 100) / 100; };

  /* "1 acre", "0.5 acres", "2.75 acres". The plural test uses the
     same 2dp precision the number is shown at. */
  function fmtAcres(n) {
    var t = n.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return t + (Math.abs(n - 1) < 0.005 ? ' acre' : ' acres');
  }

  function icon(name) {
    return name === 'check'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 6"/></svg>'
      : '';
  }

  function describe(counts, sizes) {
    var parts = [];
    for (var i = 0; i < sizes.length; i++) {
      if (counts[i] > 0) parts.push(counts[i] + ' × ' + sizes[i].lb + ' lb');
    }
    return parts.join(' + ');
  }

  function sum(arr) {
    var n = 0;
    for (var i = 0; i < arr.length; i++) n += arr[i];
    return n;
  }

  /* Cost, then waste, then bag count. */
  function better(a, b) {
    if (!b) return true;
    if (a.cost !== b.cost) return a.cost < b.cost;
    if (a.waste !== b.waste) return a.waste < b.waste;
    return a.bags < b.bags;
  }

  /* Cheapest mix of any sizes covering sqft. sizes sorted largest-first.
     rate is sq ft per lb for the chosen mode. */
  function solve(sqft, sizes, rate) {
    var n = sizes.length;
    var cov = sizes.map(function (s) { return s.lb * rate; });
    var priced = sizes.every(function (s) { return s.price != null && s.price > 0; });
    var best = null;
    var combos = 0;
    var counts = new Array(n).fill(0);

    (function walk(idx, remaining) {
      if (combos > MAX_COMBOS) return;

      if (idx === n - 1) {
        combos++;
        counts[idx] = remaining > 0 ? Math.ceil(remaining / cov[idx]) : 0;
        var covered = 0, cost = 0;
        for (var i = 0; i < n; i++) {
          covered += counts[i] * cov[i];
          cost += counts[i] * (sizes[i].price || 0);
        }
        var bags = sum(counts);
        if (covered >= sqft && bags > 0) {
          var cand = {
            counts: counts.slice(),
            cost: priced ? round2(cost) : 0,
            waste: covered - sqft,
            bags: bags
          };
          if (better(cand, best)) best = cand;
        }
        counts[idx] = 0;
        return;
      }

      var max = Math.ceil(remaining / cov[idx]);
      if (max < 0) max = 0;
      for (var k = 0; k <= max; k++) {
        counts[idx] = k;
        walk(idx + 1, remaining - k * cov[idx]);
        if (combos > MAX_COMBOS) break;
      }
      counts[idx] = 0;
    })(0, sqft);

    return best;
  }

  /* Cheapest answer using only ONE size. */
  function solveSingle(sqft, sizes, rate) {
    var best = null;
    for (var i = 0; i < sizes.length; i++) {
      var cov = sizes[i].lb * rate;
      var bags = Math.ceil(sqft / cov);
      if (bags <= 0) continue;
      var counts = new Array(sizes.length).fill(0);
      counts[i] = bags;
      var cand = {
        counts: counts,
        cost: round2((sizes[i].price || 0) * bags),
        waste: bags * cov - sqft,
        bags: bags
      };
      if (better(cand, best)) best = cand;
    }
    return best;
  }

  function setup(root) {
    var rateNew = parseFloat(root.getAttribute('data-rate-new'));
    var rateOver = parseFloat(root.getAttribute('data-rate-over'));
    /* No rate, no calculator. The rate table outputs nothing for an
       unknown product; never fall back to an invented figure. */
    if (!(rateNew > 0) || !(rateOver > 0)) { root.style.display = 'none'; return; }

    var inAcres = root.getAttribute('data-unit') === 'acres';
    var noun = root.getAttribute('data-noun') || 'lawn';

    var sizes;
    try { sizes = JSON.parse(root.getAttribute('data-sizes') || '[]'); }
    catch (e) { sizes = []; }

    sizes = sizes.filter(function (s) { return s && s.lb > 0; });
    if (!sizes.length) { root.style.display = 'none'; return; }

    sizes.sort(function (a, b) { return b.lb - a.lb; });

    var input = root.querySelector('.pt-calc__input');
    var modeEls = root.querySelectorAll('input[name^="pt-mode"]');
    var result = root.querySelector('.pt-calc__result-text');
    var grid = root.querySelector('.pt-calc__sizes');
    var single = sizes.length < 2;

    function area(sqft) {
      return inAcres ? fmtAcres(sqft / SQFT_PER_ACRE) : fmtInt(sqft) + ' sq ft';
    }

    function rateFor() {
      for (var i = 0; i < modeEls.length; i++) {
        if (modeEls[i].checked) return modeEls[i].value === 'over' ? rateOver : rateNew;
      }
      return rateNew;
    }

    /* Exposed so the recommendation can be verified from the console
       against an independent brute force. */
    root.__ptSolve = function (sqft, mode) {
      return solve(sqft, sizes, mode === 'over' ? rateOver : rateNew);
    };

    function render() {
      var raw = Math.max(0, parseFloat(input.value) || 0);
      var sqft = inAcres ? raw * SQFT_PER_ACRE : Math.round(raw);
      var rate = rateFor();

      if (grid) {
        grid.innerHTML = single ? '' : sizes.map(function (s) {
          return '<div class="pt-size" data-lb="' + s.lb + '">' +
                 '<div class="pt-size__lb">' + s.lb + ' lb</div>' +
                 '<div class="pt-size__cov">' + area(s.lb * rate) + '</div>' +
                 '<div class="pt-size__tag"></div>' +
                 '</div>';
        }).join('');
      }

      if (sqft <= 0) {
        result.textContent = single
          ? 'One ' + sizes[0].lb + ' lb bag covers about ' + area(sizes[0].lb * rate)
          : 'Enter your ' + noun + ' size for a bag recommendation';
        return;
      }

      var pick = single
        ? solveSingle(sqft, sizes, rate)
        : solve(sqft, sizes, rate);

      if (!pick) { result.textContent = 'Enter your ' + noun + ' size'; return; }

      /* Prefer a single size only when it is genuinely simpler:
         no more bags, and no more than a few dollars extra. */
      if (!single) {
        var simple = solveSingle(sqft, sizes, rate);
        if (simple && simple.cost > 0 && pick.cost > 0 &&
            simple.bags <= pick.bags &&
            simple.cost - pick.cost <= SIMPLICITY_TOLERANCE) {
          pick = simple;
        }
      }

      if (grid) {
        for (var i = 0; i < sizes.length; i++) {
          if (pick.counts[i] > 0) {
            var card = grid.querySelector('.pt-size[data-lb="' + sizes[i].lb + '"]');
            if (card) {
              card.classList.add('pt-size--best');
              var tag = card.querySelector('.pt-size__tag');
              if (tag) tag.textContent = pick.counts[i] + ' bag' + (pick.counts[i] > 1 ? 's' : '');
            }
          }
        }
      }

      var msg = describe(pick.counts, sizes) +
                (pick.bags > 1 ? ' cover your ' : ' covers your ') +
                area(sqft);

      if (pick.cost > 0) msg += ' for about ' + fmtUSD(pick.cost);
      if (pick.waste === 0) msg += ', with none left over';

      result.textContent = msg;
    }

    input.addEventListener('input', render);
    for (var i = 0; i < modeEls.length; i++) {
      modeEls[i].addEventListener('change', render);
    }
    render();
  }

  function init() {
    document.querySelectorAll('.pt-calc[data-rate-new]').forEach(function (el) {
      if (el.__ptDone) return;
      el.__ptDone = true;
      var r = el.querySelector('.pt-calc__result svg');
      if (r) r.outerHTML = icon('check');
      setup(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
